import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiEdit2, FiTrash2, FiUserCheck, FiZap } from 'react-icons/fi';
import { fetchAgents } from '../api/agentService';
import { useToast } from '../components/Toast';
import {
  assignLead,
  autoAssignLead,
  bulkAutoAssignLeads,
  deleteLead,
  fetchLeads,
  getLead,
  getSuggestedAgents,
} from '../api/leadService';
import { fetchQueues } from '../api/queueService';
import { useAuth } from '../auth/AuthContext';
import { normalizeLead } from '../utils/leadModel';

const STATUS_COLORS = {
  New: '#0ea5e9',
  Contacted: '#6366f1',
  Qualified: '#8b5cf6',
  Nurturing: '#f59e0b',
  Converted: '#10b981',
  Lost: '#f43f5e',
  Rejected: '#64748b',
};

const OWNER_BADGE_STYLES = {
  agent: { background: '#dbeafe', color: '#1d4ed8', border: '#93c5fd' },
  queue: { background: '#fef3c7', color: '#b45309', border: '#fcd34d' },
  unassigned: { background: '#e2e8f0', color: '#475569', border: '#cbd5e1' },
};

const SLA_BADGE_STYLES = {
  breached: { background: '#fee2e2', color: '#b91c1c', border: '#fecaca', label: 'SLA Breached' },
  'due-soon': { background: '#fef3c7', color: '#b45309', border: '#fde68a', label: 'Due Soon' },
  'on-track': { background: '#dcfce7', color: '#166534', border: '#86efac', label: 'On Track' },
};

const PRIORITY_RANK = {
  Low: 1,
  Medium: 2,
  High: 3,
  Urgent: 4,
};

const STATUS_RANK = {
  New: 1,
  Contacted: 2,
  Qualified: 3,
  Nurturing: 4,
  Converted: 5,
  Lost: 6,
  Rejected: 7,
};

function toTitle(value = '') {
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function pillStyle({ background, color, border }) {
  return {
    background,
    color,
    border: `1px solid ${border}`,
    padding: '2px 10px',
    borderRadius: 99,
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  };
}

function StatusBadge({ status }) {
  const color = STATUS_COLORS[status] || '#94a3b8';
  return <span style={pillStyle({ background: `${color}20`, color, border: `${color}40` })}>{status}</span>;
}

function OwnerBadge({ ownerType, label }) {
  const palette = OWNER_BADGE_STYLES[ownerType] || OWNER_BADGE_STYLES.unassigned;
  return <span style={pillStyle(palette)}>{label}</span>;
}

function SlaBadge({ lead }) {
  if (!lead?.slaDueAt || lead.slaState === 'none') {
    return <span style={{ color: '#64748b' }}>—</span>;
  }

  const palette = SLA_BADGE_STYLES[lead.slaState] || SLA_BADGE_STYLES['on-track'];
  return (
    <span style={pillStyle(palette)} title={lead.slaDueAtLabel}>
      {palette.label}
    </span>
  );
}

function AssignmentHistory({ history }) {
  if (!history?.length) {
    return <p style={{ margin: 0, color: '#64748b', fontSize: 14 }}>No assignment activity yet.</p>;
  }

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {history.map((item, index) => (
        <div
          key={`${item.assignedAt || 'event'}-${index}`}
          style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
            <strong style={{ color: '#0f172a', fontSize: 13 }}>{toTitle(item.method || item.ownerType)}</strong>
            <span style={{ color: '#64748b', fontSize: 12 }}>{item.assignedAtLabel}</span>
          </div>
          <div style={{ color: '#334155', fontSize: 13 }}>
            {item.reason || 'Assignment updated.'}
          </div>
        </div>
      ))}
    </div>
  );
}

function SuggestedAgentsModal({ leadId, onClose, onAssigned }) {
  const toast = useToast();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submittingAgentId, setSubmittingAgentId] = useState(null);

  useEffect(() => {
    let mounted = true;

    async function loadSuggestions() {
      setLoading(true);
      setError('');
      try {
        const response = await getSuggestedAgents(leadId);
        if (!mounted) {
          return;
        }
        setSuggestions(response.suggestions || response.data?.suggestions || []);
      } catch (err) {
        if (mounted) {
          setError(err?.response?.data?.error || err?.response?.data?.message || 'Unable to load suggested agents.');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    loadSuggestions();
    return () => {
      mounted = false;
    };
  }, [leadId]);

  const handleAssign = async (agentId) => {
    setSubmittingAgentId(agentId);
    try {
      await assignLead(leadId, agentId);
      onAssigned();
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Manual assignment failed.');
      setSubmittingAgentId(null);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <h3>Suggested Agents</h3>
          <button className="modal-close" type="button" onClick={onClose} aria-label="Close">x</button>
        </div>

        {loading ? <p>Loading suggested agents…</p> : null}
        {error ? <p className="error-text">{error}</p> : null}

        {!loading && !error && !suggestions.length ? (
          <p style={{ color: '#64748b' }}>No compatible agents were returned for this lead.</p>
        ) : null}

        {!loading && suggestions.length ? (
          <div style={{ display: 'grid', gap: 12 }}>
            {suggestions.map((agent) => (
              <div
                key={agent.agent_id}
                style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 16px', display: 'grid', gap: 8 }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                  <div>
                    <strong style={{ display: 'block', color: '#0f172a' }}>{agent.name}</strong>
                    <span style={{ color: '#64748b', fontSize: 13 }}>{agent.email}</span>
                  </div>
                  <span style={pillStyle({ background: '#eef2ff', color: '#4338ca', border: '#c7d2fe' })}>
                    Score {agent.score}
                  </span>
                </div>
                <div style={{ color: '#334155', fontSize: 13 }}>
                  Load {agent.current_leads}/{agent.max_capacity} · {toTitle(agent.availability || 'available')}
                </div>
                <div style={{ color: '#475569', fontSize: 13 }}>
                  {(agent.reasons || []).map(toTitle).join(', ') || 'No match reasons returned.'}
                </div>
                <div>
                  <button
                    className="primary-btn"
                    type="button"
                    disabled={submittingAgentId === agent.agent_id}
                    onClick={() => handleAssign(agent.agent_id)}
                  >
                    {submittingAgentId === agent.agent_id ? 'Assigning…' : 'Assign to Agent'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function LeadDrawer({
  leadId,
  canManageAssignment,
  onClose,
  onDeleted,
  onEdit,
  onLeadUpdated,
  agentNameById,
  queueNameById,
}) {
  const toast = useToast();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const loadLead = useCallback(async () => {
    if (!leadId) {
      return;
    }

    setLoading(true);
    setLead(null);
    setConfirmDelete(false);
    try {
      const response = await getLead(leadId);
      setLead(normalizeLead(response.data || response));
    } catch {
      setLead(null);
      toast.error('Unable to load lead details.');
    } finally {
      setLoading(false);
    }
  }, [leadId, toast]);

  useEffect(() => {
    loadLead();
  }, [loadLead]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteLead(leadId);
      onDeleted(leadId);
      onClose();
    } catch (err) {
      setDeleting(false);
      setConfirmDelete(false);
      toast.error(err?.response?.data?.message || 'Lead deletion failed.');
    }
  };

  const handleAutoAssign = async () => {
    setAssigning(true);
    try {
      const response = await autoAssignLead(leadId);
      const nextLead = response.lead || response.data?.lead || response.data || response;
      if (nextLead?.id || nextLead?._id) {
        setLead(normalizeLead(nextLead));
      } else {
        await loadLead();
      }
      toast.success(response.message || 'Lead assignment updated.');
      onLeadUpdated();
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Auto-assign failed.');
    } finally {
      setAssigning(false);
    }
  };

  const handleManualAssigned = async () => {
    setShowSuggestions(false);
    toast.success('Lead assigned successfully.');
    await loadLead();
    onLeadUpdated();
  };

  const ownerDisplay =
    lead?.ownerType === 'queue'
      ? queueNameById[lead.assignmentQueueId] || lead.assignmentQueueId || 'Queue'
      : lead?.ownerType === 'agent'
        ? agentNameById[lead.assignedTo] || lead.assignedTo || 'Assigned agent'
        : 'Unassigned';

  const row = (label, value) => (
    <div className="drawer-row">
      <span className="drawer-label">{label}</span>
      <span className="drawer-value">{value || '—'}</span>
    </div>
  );

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <aside className="lead-drawer">
        <div className="drawer-header">
          <h3>{loading ? 'Loading…' : lead?.fullName || 'Lead Detail'}</h3>
          <button className="modal-close" type="button" onClick={onClose} aria-label="Close">×</button>
        </div>

        {loading ? <p className="drawer-loading">Loading…</p> : null}

        {!loading && lead ? (
          <>
            <div className="drawer-section">
              <p className="drawer-section-title">Contact</p>
              {row('Full Name', lead.fullName)}
              {row('Email', lead.email)}
              {row('Phone', lead.phone)}
              {row('Company', lead.companyName)}
            </div>

            <div className="drawer-section">
              <p className="drawer-section-title">Assignment</p>
              <div className="drawer-row">
                <span className="drawer-label">Owner</span>
                <span className="drawer-value"><OwnerBadge ownerType={lead.ownerType} label={ownerDisplay} /></span>
              </div>
              <div className="drawer-row">
                <span className="drawer-label">SLA</span>
                <span className="drawer-value"><SlaBadge lead={lead} /></span>
              </div>
              {lead.slaDueAt ? row('SLA Due At', lead.slaDueAtLabel) : null}
              {lead.claimedAt ? row('Claimed At', lead.claimedAtLabel) : null}
              {lead.assignmentRuleId ? row('Rule ID', lead.assignmentRuleId) : null}
              {lead.leadScore != null ? row('Lead Score', lead.leadScore) : null}
            </div>

            <div className="drawer-section">
              <p className="drawer-section-title">Lead Info</p>
              {row('Source', lead.source)}
              {row('Priority', lead.priority)}
              <div className="drawer-row">
                <span className="drawer-label">Status</span>
                <StatusBadge status={lead.status} />
              </div>
              {lead.leadClassification ? row('Classification', toTitle(lead.leadClassification)) : null}
            </div>

            <div className="drawer-section">
              <p className="drawer-section-title">Assignment History</p>
              <AssignmentHistory history={lead.assignmentHistory} />
            </div>

            <div className="drawer-section">
              <p className="drawer-section-title">Timeline</p>
              {row('Created', lead.createdAtLabel)}
              {row('Updated', lead.updatedAtLabel)}
            </div>

            <div className="drawer-actions">
              <button className="primary-btn" type="button" onClick={() => onEdit(lead.id)}>
                Edit Lead
              </button>

              {canManageAssignment ? (
                <button className="ghost-btn" type="button" disabled={assigning} onClick={handleAutoAssign}>
                  {assigning ? 'Assigning…' : 'Auto-Assign'}
                </button>
              ) : null}

              {canManageAssignment ? (
                <button className="ghost-btn" type="button" onClick={() => setShowSuggestions(true)}>
                  Assign Manually
                </button>
              ) : null}

              {!confirmDelete ? (
                <button className="danger-btn" type="button" onClick={() => setConfirmDelete(true)}>
                  Delete
                </button>
              ) : (
                <div className="delete-confirm">
                  <span className="delete-confirm-msg">Delete this lead?</span>
                  <button className="danger-btn" type="button" disabled={deleting} onClick={handleDelete}>
                    {deleting ? 'Deleting…' : 'Confirm Delete'}
                  </button>
                  <button className="ghost-btn" type="button" onClick={() => setConfirmDelete(false)}>
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </>
        ) : null}
      </aside>

      {showSuggestions ? (
        <SuggestedAgentsModal
          leadId={leadId}
          onClose={() => setShowSuggestions(false)}
          onAssigned={handleManualAssigned}
        />
      ) : null}
    </>
  );
}

function LeadsPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { role } = useAuth();
  const [leads, setLeads] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterOwnerType, setFilterOwnerType] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [activeLeadId, setActiveLeadId] = useState(null);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [agentNameById, setAgentNameById] = useState({});
  const [queueNameById, setQueueNameById] = useState({});
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  const canManageAssignment = role === 'admin' || role === 'manager';

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;
      if (filterPriority) params.priority = filterPriority;
      const response = await fetchLeads(params);
      setLeads(Array.isArray(response.data) ? response.data.map(normalizeLead) : []);
      setPagination(response.pagination || null);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to load leads.');
    } finally {
      setLoading(false);
    }
  }, [filterPriority, filterStatus, page, search, toast]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  useEffect(() => {
    let mounted = true;

    async function loadMetadata() {
      try {
        const [queueResponse, agentResponse] = await Promise.all([
          fetchQueues(),
          fetchAgents(),
        ]);
        if (!mounted) {
          return;
        }

        const queues = queueResponse.queues || queueResponse.data?.queues || [];
        const nextQueueMap = queues.reduce((accumulator, queue) => {
          accumulator[queue.id] = queue.name;
          return accumulator;
        }, {});
        setQueueNameById(nextQueueMap);

        const agents = agentResponse.data || agentResponse.agents || [];
        const nextAgentMap = agents.reduce((accumulator, agent) => {
          accumulator[agent.id] = agent.name;
          return accumulator;
        }, {});
        setAgentNameById(nextAgentMap);
      } catch {
        if (mounted) {
          setAgentNameById({});
          setQueueNameById({});
        }
      }
    }

    loadMetadata();
    return () => {
      mounted = false;
    };
  }, []);

  const visibleLeads = useMemo(() => {
    if (!filterOwnerType) {
      return leads;
    }
    return leads.filter((lead) => lead.ownerType === filterOwnerType);
  }, [filterOwnerType, leads]);

  const sortedVisibleLeads = useMemo(() => {
    const sorted = [...visibleLeads];
    sorted.sort((left, right) => {
      let comparison = 0;

      if (sortBy === 'name') {
        comparison = (left.fullName || '').localeCompare(right.fullName || '', undefined, { sensitivity: 'base' });
      } else if (sortBy === 'priority') {
        comparison = (PRIORITY_RANK[left.priority] || 0) - (PRIORITY_RANK[right.priority] || 0);
      } else if (sortBy === 'status') {
        comparison = (STATUS_RANK[left.status] || 0) - (STATUS_RANK[right.status] || 0);
      }

      if (comparison === 0) {
        comparison = (left.fullName || '').localeCompare(right.fullName || '', undefined, { sensitivity: 'base' });
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
    return sorted;
  }, [sortBy, sortDirection, visibleLeads]);

  const selectedSet = useMemo(() => new Set(selectedLeadIds), [selectedLeadIds]);
  const allVisibleSelected = sortedVisibleLeads.length > 0 && sortedVisibleLeads.every((lead) => selectedSet.has(lead.id));

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(field);
    setSortDirection('asc');
  };

  const getSortArrow = (field) => {
    if (sortBy !== field) {
      return '↕';
    }
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const handleDeleted = (id) => {
    setLeads((prev) => prev.filter((lead) => lead.id !== id));
    setSelectedLeadIds((prev) => prev.filter((leadId) => leadId !== id));
    toast.success('Lead deleted.');
  };

  const handleLeadUpdated = useCallback(async () => {
    await loadLeads();
  }, [loadLeads]);

  const toggleSelectedLead = (leadId) => {
    setSelectedLeadIds((prev) => (
      prev.includes(leadId) ? prev.filter((id) => id !== leadId) : [...prev, leadId]
    ));
  };

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedLeadIds((prev) => prev.filter((leadId) => !sortedVisibleLeads.some((lead) => lead.id === leadId)));
      return;
    }

    setSelectedLeadIds((prev) => Array.from(new Set([...prev, ...sortedVisibleLeads.map((lead) => lead.id)])));
  };

  const handleSingleAutoAssign = async (leadId) => {
    try {
      const response = await autoAssignLead(leadId);
      toast.success(response.message || 'Lead assignment updated.');
      await loadLeads();
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Auto-assign failed.');
    }
  };

  const handleBulkAutoAssign = async (leadIds) => {
    setBulkActionLoading(true);
    try {
      const response = await bulkAutoAssignLeads(leadIds);
      toast.success(
        response.message
          || `Processed ${response.processed || leadIds?.length || 0} leads. Assigned ${response.assigned_to_agent || 0} to agents and ${response.assigned_to_queue || 0} to queues.`
      );
      setSelectedLeadIds([]);
      await loadLeads();
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Bulk auto-assign failed.');
    } finally {
      setBulkActionLoading(false);
    }
  };

  return (
    <div>
      <div className="page-title-row">
        <h2>Lead Pipeline</h2>
        <Link className="primary-link" to="/leads/new">+ New Lead</Link>
      </div>

      <div className="filter-bar">
        <input
          className="filter-input"
          placeholder="Search name, email, company…"
          value={search}
          onChange={(event) => { setSearch(event.target.value); setPage(1); }}
        />
        <select
          className="filter-select"
          value={filterStatus}
          onChange={(event) => { setFilterStatus(event.target.value); setPage(1); }}
        >
          <option value="">All Statuses</option>
          {['New', 'Contacted', 'Qualified', 'Nurturing', 'Converted', 'Lost', 'Rejected'].map((status) => (
            <option key={status} value={status}>{status}</option>
          ))}
        </select>
        <select
          className="filter-select"
          value={filterPriority}
          onChange={(event) => { setFilterPriority(event.target.value); setPage(1); }}
        >
          <option value="">All Priorities</option>
          {['Low', 'Medium', 'High', 'Urgent'].map((priority) => (
            <option key={priority} value={priority}>{priority}</option>
          ))}
        </select>
        <select
          className="filter-select"
          value={filterOwnerType}
          onChange={(event) => setFilterOwnerType(event.target.value)}
        >
          <option value="">All Owners</option>
          <option value="agent">Agent Owned</option>
          <option value="queue">Queue Owned</option>
          <option value="unassigned">Unassigned</option>
        </select>
      </div>

      {canManageAssignment ? (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          <button
            className="ghost-btn"
            type="button"
            disabled={bulkActionLoading || selectedLeadIds.length === 0}
            onClick={() => handleBulkAutoAssign(selectedLeadIds)}
          >
            {bulkActionLoading ? 'Processing…' : `Auto-Assign Selected (${selectedLeadIds.length})`}
          </button>
          <button
            className="ghost-btn"
            type="button"
            disabled={bulkActionLoading}
            onClick={() => handleBulkAutoAssign()}
          >
            {bulkActionLoading ? 'Processing…' : 'Auto-Assign All Unassigned'}
          </button>
        </div>
      ) : null}

      {loading ? <p>Loading leads…</p> : null}

      {!loading ? (
        <>
          <table className="data-table lead-pipeline-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    aria-label="Select visible leads"
                  />
                </th>
                <th>
                  <button className="table-sort-btn" type="button" onClick={() => toggleSort('name')}>
                    Name {getSortArrow('name')}
                  </button>
                </th>
                <th>Email</th>
                <th>Company</th>
                <th>Owner</th>
                <th>SLA</th>
                <th>
                  <button className="table-sort-btn" type="button" onClick={() => toggleSort('priority')}>
                    Priority {getSortArrow('priority')}
                  </button>
                </th>
                <th>
                  <button className="table-sort-btn" type="button" onClick={() => toggleSort('status')}>
                    Status {getSortArrow('status')}
                  </button>
                </th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedVisibleLeads.length ? (
                sortedVisibleLeads.map((lead) => {
                  const ownerLabel =
                    lead.ownerType === 'queue'
                      ? queueNameById[lead.assignmentQueueId] || lead.assignmentQueueId || 'Queue'
                      : lead.ownerType === 'agent'
                        ? agentNameById[lead.assignedTo] || lead.assignedTo || 'Assigned agent'
                        : 'Unassigned';

                  return (
                    <tr key={lead.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedSet.has(lead.id)}
                          onChange={() => toggleSelectedLead(lead.id)}
                          aria-label={`Select ${lead.fullName}`}
                        />
                      </td>
                      <td>
                        <button
                          className="lead-name-btn"
                          type="button"
                          onClick={() => setActiveLeadId(lead.id)}
                        >
                          {lead.fullName}
                        </button>
                      </td>
                      <td>{lead.email}</td>
                      <td>{lead.companyName}</td>
                      <td><OwnerBadge ownerType={lead.ownerType} label={ownerLabel} /></td>
                      <td><SlaBadge lead={lead} /></td>
                      <td>{lead.priority}</td>
                      <td><StatusBadge status={lead.status} /></td>
                      <td>{lead.updatedAtLabel}</td>
                      <td>
                        <div className="table-actions">
                          <button
                            type="button"
                            className="icon-action-btn"
                            title="Edit lead"
                            aria-label="Edit lead"
                            data-tooltip="Edit lead"
                            onClick={() => navigate(`/leads/${lead.id}/edit`)}
                          >
                            <FiEdit2 />
                          </button>
                          {canManageAssignment ? (
                            <button
                              type="button"
                              className="icon-action-btn"
                              title="Auto-assign lead"
                              aria-label="Auto-assign lead"
                              data-tooltip="Auto-assign lead"
                              onClick={() => handleSingleAutoAssign(lead.id)}
                            >
                              <FiZap />
                            </button>
                          ) : null}
                          {canManageAssignment ? (
                            <button
                              type="button"
                              className="icon-action-btn"
                              title="Manual assign"
                              aria-label="Manual assign"
                              data-tooltip="Manual assign"
                              onClick={() => setActiveLeadId(lead.id)}
                            >
                              <FiUserCheck />
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="icon-action-btn icon-action-btn--danger"
                            title="Delete lead"
                            aria-label="Delete lead"
                            data-tooltip="Delete lead"
                            onClick={() => setActiveLeadId(lead.id)}
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr><td colSpan="10">No leads found.</td></tr>
              )}
            </tbody>
          </table>

          {pagination && pagination.total_pages > 1 ? (
            <div className="pagination-row">
              <button
                className="ghost-btn"
                type="button"
                disabled={!pagination.has_prev_page}
                onClick={() => setPage((currentPage) => currentPage - 1)}
              >
                ← Previous
              </button>
              <span>
                Page {pagination.current_page} of {pagination.total_pages}
                {' '}({pagination.total_items} total)
              </span>
              <button
                className="ghost-btn"
                type="button"
                disabled={!pagination.has_next_page}
                onClick={() => setPage((currentPage) => currentPage + 1)}
              >
                Next →
              </button>
            </div>
          ) : null}
        </>
      ) : null}

      {activeLeadId ? (
        <LeadDrawer
          leadId={activeLeadId}
          canManageAssignment={canManageAssignment}
          onClose={() => setActiveLeadId(null)}
          onDeleted={handleDeleted}
          onEdit={(id) => { setActiveLeadId(null); navigate(`/leads/${id}/edit`); }}
          onLeadUpdated={handleLeadUpdated}
          agentNameById={agentNameById}
          queueNameById={queueNameById}
        />
      ) : null}
    </div>
  );
}

export default LeadsPage;
